import { CONTRACT_STATUSES } from '@beta-assur/shared';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { ContractStatusBadge } from '../components/badges';
import { PageHeader } from '../components/page';
import { Empty, ErrorState, Loading } from '../components/states';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input, Select } from '../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { day, money } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useCursorList } from '../lib/infinite';
import { useReferentials } from '../lib/referentials';
import type { Contract, Policyholder } from '../lib/types';
import { PolicyholderCreateDialog } from './portfolio-forms';

// Contrats et assurés (BA-023, spec 08 §3).

export const portfolioSearchSchema = z.object({
  tab: z.enum(['contrats', 'assures']).optional(),
  q: z.coerce.string().optional(),
  policyNumber: z.coerce.string().optional(),
  status: z.enum(CONTRACT_STATUSES).optional(),
  insurerId: z.string().optional(),
});
type PortfolioSearch = z.infer<typeof portfolioSearchSchema>;

export function Tabs<K extends string>({
  tabs,
  active,
  onChange,
  label,
}: {
  tabs: { key: K; label: string }[];
  active: K;
  onChange: (key: K) => void;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="mb-4 flex flex-wrap gap-1 border-b border-border"
    >
      {tabs.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          type="button"
          aria-selected={tab.key === active}
          onClick={() => onChange(tab.key)}
          className={cn(
            '-mb-px cursor-pointer border-b-2 px-4 py-2 text-sm',
            tab.key === active
              ? 'border-primary font-medium text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function ContractList({ search }: { search: PortfolioSearch }) {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const refs = useReferentials();
  const [policyNumber, setPolicyNumber] = useState(search.policyNumber ?? '');
  const query = {
    policyNumber: search.policyNumber,
    status: search.status,
    insurerId: search.insurerId,
  };
  const list = useCursorList<Contract>('/v1/contracts', query);

  const go = (patch: Partial<PortfolioSearch>) =>
    void navigate({ to: '/portefeuille', search: { ...search, tab: 'contrats', ...patch } });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    go({ policyNumber: policyNumber.trim() || undefined });
  };

  return (
    <>
      <form onSubmit={submit} className="mb-4 flex flex-wrap gap-2">
        <Input
          aria-label={t.portfolio.searchContracts}
          placeholder={t.portfolio.searchContracts}
          className="max-w-xs"
          value={policyNumber}
          onChange={(e) => setPolicyNumber(e.target.value)}
        />
        <Select
          aria-label={t.portfolio.status}
          className="w-auto"
          value={search.status ?? ''}
          onChange={(e) =>
            go({ status: (e.target.value || undefined) as PortfolioSearch['status'] })
          }
        >
          <option value="">
            {t.portfolio.status} : {t.common.all}
          </option>
          {CONTRACT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t.contractStatus[s]}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t.portfolio.insurer}
          className="w-auto"
          value={search.insurerId ?? ''}
          onChange={(e) => go({ insurerId: e.target.value || undefined })}
        >
          <option value="">
            {t.portfolio.insurer} : {t.common.all}
          </option>
          {refs.insurers.map((i) => (
            <option key={i.id} value={i.id}>
              {i.displayName}
            </option>
          ))}
        </Select>
        <Button type="submit">{t.common.search}</Button>
      </form>
      <Card>
        {list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.isPending ? (
          <Loading />
        ) : list.items.length === 0 ? (
          <Empty />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>{t.portfolio.policyNumber}</TH>
                <TH>{t.portfolio.insurer}</TH>
                <TH>{t.portfolio.product}</TH>
                <TH>{t.portfolio.effectiveDate}</TH>
                <TH>{t.portfolio.periodicity}</TH>
                <TH className="text-end">{t.portfolio.premium}</TH>
                <TH>{t.portfolio.status}</TH>
              </tr>
            </THead>
            <TBody>
              {list.items.map((c) => (
                <TR key={c.id}>
                  <TD>
                    <Link
                      to="/contrats/$id"
                      params={{ id: c.id }}
                      className="font-medium whitespace-nowrap text-primary hover:underline"
                    >
                      {c.policyNumber}
                    </Link>
                  </TD>
                  <TD>{refs.insurerName(c.insurerId)}</TD>
                  <TD>{refs.productName(c.productLineId)}</TD>
                  <TD>{day(c.effectiveDate, language)}</TD>
                  <TD>{t.periodicity[c.periodicity]}</TD>
                  <TD className="text-end whitespace-nowrap tabular-nums">
                    {money(c.premiumAmount, language)}
                  </TD>
                  <TD>
                    <ContractStatusBadge status={c.status} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
        {list.hasNextPage ? (
          <div className="border-t border-border p-3 text-center">
            <Button variant="outline" onClick={() => void list.fetchNextPage()}>
              {t.common.loadMore}
            </Button>
          </div>
        ) : null}
      </Card>
    </>
  );
}

function PolicyholderList({ search }: { search: PortfolioSearch }) {
  const t = useI18n().t;
  const navigate = useNavigate();
  const [q, setQ] = useState(search.q ?? '');
  const list = useCursorList<Policyholder>('/v1/policyholders', { q: search.q });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = q.trim();
    void navigate({
      to: '/portefeuille',
      search: { tab: 'assures', q: value.length >= 2 ? value : undefined },
    });
  };

  return (
    <>
      <form onSubmit={submit} className="mb-4 flex flex-wrap gap-2">
        <Input
          aria-label={t.portfolio.searchPolicyholders}
          placeholder={t.portfolio.searchPolicyholders}
          className="max-w-sm"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Button type="submit">{t.common.search}</Button>
        <p className="w-full text-xs text-muted-foreground">{t.portfolio.searchHint}</p>
      </form>
      <Card>
        {list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.isPending ? (
          <Loading />
        ) : list.items.length === 0 ? (
          <Empty />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>{t.installments.policyholder}</TH>
                <TH>{t.portfolio.phone}</TH>
                <TH>{t.portfolio.email}</TH>
                <TH>{t.portfolio.preferredChannel}</TH>
                <TH>{t.portfolio.externalRef}</TH>
              </tr>
            </THead>
            <TBody>
              {list.items.map((p) => (
                <TR key={p.id}>
                  <TD>
                    <Link
                      to="/assures/$id"
                      params={{ id: p.id }}
                      className="font-medium whitespace-nowrap text-primary hover:underline"
                    >
                      {p.displayName}
                    </Link>
                  </TD>
                  <TD dir="ltr" className="text-start">
                    {p.phone ?? '-'}
                  </TD>
                  <TD>{p.email ?? '-'}</TD>
                  <TD>{t.channels[p.preferredChannel]}</TD>
                  <TD>{p.externalRef ?? '-'}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
        {list.hasNextPage ? (
          <div className="border-t border-border p-3 text-center">
            <Button variant="outline" onClick={() => void list.fetchNextPage()}>
              {t.common.loadMore}
            </Button>
          </div>
        ) : null}
      </Card>
    </>
  );
}

export function PortfolioPage() {
  const t = useI18n().t;
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as PortfolioSearch;
  const tab = search.tab ?? 'contrats';
  return (
    <>
      <PageHeader
        title={t.portfolio.title}
        actions={
          auth.can('editPortfolio') && tab === 'assures' ? <PolicyholderCreateDialog /> : null
        }
      />
      <Tabs
        label={t.portfolio.title}
        active={tab}
        onChange={(key) => void navigate({ to: '/portefeuille', search: { tab: key } })}
        tabs={[
          { key: 'contrats', label: t.portfolio.contracts },
          { key: 'assures', label: t.portfolio.policyholders },
        ]}
      />
      {tab === 'contrats' ? <ContractList search={search} /> : <PolicyholderList search={search} />}
    </>
  );
}
